import type { Moment } from 'moment';
import moment from 'moment';

import type { Range } from '../../../layers/model';
import { generateKeyFromRange, StorageKeyManager } from '../../../shared/storage-key-manager';
import { TimeRangeHelper } from '../../../shared/time-range-helper';
import { TimeUnit } from '../../../shared/time-unit';
import { getTimeUnit } from '../../../shared/viewport/viewport-calculator';

import type { IHeaderColumn } from './header-data-model.types';
import { MonthHeaderColumn, YearHeaderColumn } from './model/header-column';

const getMonthColumns = (
  range: Range<Moment>,
  keyManager: StorageKeyManager<Range<number>, string>,
  previousColumnsMap: Map<string, MonthHeaderColumn>,
): MonthHeaderColumn[] => {
  const monthColumns: MonthHeaderColumn[] = [];
  const lastMonthUnix = range.end.startOf('month').unix();

  let monthCount = 0;
  let currentMonth = range.start;

  do {
    currentMonth = moment(range.start).add({ month: monthCount });

    const start = currentMonth.startOf('month').unix();
    const end = currentMonth.endOf('month').unix();

    const rangeKey = keyManager.getKey({ start, end });
    const oldColumn = previousColumnsMap.get(rangeKey);

    monthColumns.push(oldColumn ?? new MonthHeaderColumn(currentMonth.month(), start, end));

    monthCount++;
  } while (moment(currentMonth).startOf('month').unix() < lastMonthUnix);

  return monthColumns;
};

// eslint-disable-next-line @typescript-eslint/no-namespace
namespace HeaderDataSource {
  export const getDateColumnsInPeriod = (
    range: Range<Moment>,
    timeUnit: TimeUnit,
    previousColumns?: IHeaderColumn[],
  ): IHeaderColumn[] => {
    const keyManager = new StorageKeyManager(generateKeyFromRange);

    switch (timeUnit) {
      case TimeUnit.month: {
        const previousColumnsMap = (() => {
          if (!previousColumns?.every((column) => column instanceof MonthHeaderColumn)) {
            return new Map<string, MonthHeaderColumn>();
          }

          return new Map<string, MonthHeaderColumn>(
            previousColumns?.map((column): [string, MonthHeaderColumn] => {
              const rangeKey = keyManager.getKey({ start: column.start, end: column.end });
              return [rangeKey, column];
            }),
          );
        })();

        return getMonthColumns(range, keyManager, previousColumnsMap);
      }
      case TimeUnit.year: {
        const previousColumnsMap = (() => {
          if (!previousColumns?.every((column) => column instanceof YearHeaderColumn)) {
            return new Map<string, YearHeaderColumn>();
          }

          return new Map<string, YearHeaderColumn>(
            previousColumns?.map((column): [string, YearHeaderColumn] => {
              const rangeKey = keyManager.getKey({ start: column.start, end: column.end });
              return [rangeKey, column];
            }),
          );
        })();

        const startYear = range.start.get('year');
        const endYear = range.end.get('year');

        const yearColumns: YearHeaderColumn[] = [];

        for (let year = startYear; year <= endYear; year++) {
          const currentYear = moment().set('year', year);

          const start = currentYear.startOf('year').unix();
          const end = currentYear.endOf('year').unix();

          const rangeKey = keyManager.getKey({ start, end });
          const oldColumn = previousColumnsMap.get(rangeKey);

          const column = oldColumn ?? new YearHeaderColumn(year, start, end);

          column.setMonthHeaderColumns(
            getMonthColumns(
              { start: moment.unix(start), end: moment.unix(end) },
              keyManager,
              new Map<string, MonthHeaderColumn>(
                column.monthHeaderColumns.map((monthColumn) => {
                  const monthColumnRangeKey = keyManager.getKey({ start: monthColumn.start, end: monthColumn.end });
                  return [monthColumnRangeKey, monthColumn];
                }),
              ),
            ),
          );
          yearColumns.push(column);
        }

        return yearColumns;
      }
      default:
        return [];
    }
  };

  export const generateColumns = (viewRange: Range<number>, previousColumns?: IHeaderColumn[]): IHeaderColumn[] => {
    const viewStartMoment = moment.unix(viewRange.start);
    const viewEndMoment = moment.unix(viewRange.end);

    const timeUnit = getTimeUnit(viewRange);

    const expandedRange = TimeRangeHelper.expandRange({ start: viewStartMoment, end: viewEndMoment }, timeUnit);

    return getDateColumnsInPeriod(expandedRange, timeUnit, previousColumns);
  };
}

export default HeaderDataSource;
